<template>
  <div class="table">
    <div class="table-header" v-if="filteredTickets.length > 0">
      <div class="table-header-cell round">
        <span>{{ $t('Round') }}</span>
      </div>
      <div class="table-header-cell bet">{{ $t('Bet') }}</div>
      <div class="table-header-cell market"></div>
      <div class="table-header-cell odds">{{ $t('Odds') }}</div>
      <div class="table-header-cell total">{{ $t('Total') }}</div>
    </div>
    <div class="table-body">
      <template v-for="(ticket, index) in rows">
        <!--
          Date divider, as v1: the day and its time range with the day's Total,
          then its Jackpot / Ante / Commission / W-L. A range wider than one day
          ("Last week") reads as one block per day.
        -->
        <div v-if="ticket.type === 'general'" :key="`day-${ticket.dateVal}-${index}`" class="day-divider">
          <div class="day-divider__head">
            <div class="day-divider__date">
              <span>{{ ticket.date }}</span>
              <!-- <span class="day-divider__range">{{ realTimeRange(ticket.dateVal) }}</span> -->
            </div>
            <div class="day-divider__total" :class="signClass(dayTotal(ticket))">
              {{ currencySymbol }} {{ formatSigned(dayTotal(ticket)) }}
            </div>
          </div>
          <div class="day-divider__details">
            <div class="day-divider__item">
              <span class="day-divider__label">{{ $t('Jackpot') }}</span>
              <span :class="signClass(ticket.jackpot)">{{ currencySymbol }} {{ formatAmount(ticket.jackpot, 2) }}</span>
            </div>
            <div class="day-divider__item">
              <span class="day-divider__label">{{ $t('Ante') }} ({{ taxPercent }}%)</span>
              <span class="lose">{{ currencySymbol }} -{{ formatAmount(ticket.ante, 2) }}</span>
            </div>
            <div v-if="isCreditUser || hasCommission" class="day-divider__item">
              <span class="day-divider__label">{{ $t('Commission') }}</span>
              <span>{{ currencySymbol }} {{ formatAmount(ticket.commission, 2) }}</span>
            </div>
            <div class="day-divider__item day-divider__item--end">
              <span class="day-divider__label">{{ showEarnings ? $t('Earning') : 'W/L' }}</span>
              <span :class="signClass(showEarnings ? ticket.earnings : ticket.wl)">
                {{ currencySymbol }} {{ formatSigned(showEarnings ? ticket.earnings : ticket.wl) }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-else
          :key="`${ticket.id}-${index}`"
          class="table-row"
          :class="{ even: ticket.stripe % 2 === 1, 'is-multi': isMulti(ticket) }"
          @click="viewBetDetail(ticket)"
        >
          <!-- Arena and round, with the time under it (e.g. txs#3 / 13:20). -->
          <div class="table-cell round">
            <span class="round__id">{{ ticket.id }}</span>
            <span class="round__time">{{ formatTime(ticket.date) }}</span>
          </div>
          <div class="table-cell bet">{{ formatBetAmount(ticket.bet) }}</div>
          <div class="table-cell market">
            <!-- Multi-selection ticket: "N BETS", which opens its selections below the row. -->
            <button
              v-if="isMulti(ticket)"
              type="button"
              class="bets-toggle"
              :class="{ open: isExpanded(ticket) }"
              @click.stop="toggleExpanded(ticket)"
            >
              {{ subTickets(ticket).length }} {{ $t('Bets') }}
              <span class="bets-toggle__caret" aria-hidden="true"></span>
            </button>
            <!-- Tài Xỉu triple / total / single / double: an icon badge. -->
            <TxMarketBadge v-else-if="iconMarket(ticket)" :market="iconMarket(ticket)" />
            <span v-else class="market-badge" :class="marketBadgeClass(ticket)">
              {{ marketLabel(ticket) }}
            </span>
          </div>
          <div class="table-cell odds">
            <!-- Each selection has its own odds; they are in the rows below. -->
            <span v-if="isMulti(ticket)" class="odds-multi">—</span>
            <span v-else-if="Number(ticket.odds) !== 0">{{ oddsText(ticket) }}</span>
          </div>
          <div class="table-cell total" :class="totalClass(ticket)">
            <!-- A multi-selection ticket still in play: PENDING, with a chevron to its detail. -->
            <span v-if="isMulti(ticket) && isPending(ticket)" class="multi-status">
              {{ $t('Pending') }}<span class="multi-status__chevron" aria-hidden="true">›</span>
            </span>
            <template v-else-if="hasNumericTotal(ticket)">
              {{ formatSignedTotal(ticket) }}
            </template>
            <span
              v-else
              class="result-status"
              :class="{
                cancelled: ticket.raw.result === 'cancelled',
                void: ticket.raw.result === 'void',
                draw: ticket.raw.fight.result === 'draw',
              }"
            >
              {{
                $t(
                  $capitalize(
                    ['cancelled', 'void'].includes(ticket.raw.result)
                      ? ticket.raw.result
                      : ['processed', 'processing'].includes(ticket.raw.fight.status)
                      ? ticket.raw.fight.result
                      : ''
                  )
                )
              }}
            </span>
          </div>
        </div>
        <!-- A multi-selection ticket's selections, under its row while it is open. -->
        <div
          v-if="ticket.type !== 'general' && isMulti(ticket) && isExpanded(ticket)"
          :key="`${ticket.raw.id}-subs-${index}`"
          class="sub-rows"
        >
          <div v-for="sub in subTickets(ticket)" :key="sub.id" class="sub-row">
            <div class="sub-row__market">
              <TxMarketBadge v-if="subIconMarket(sub)" :market="subIconMarket(sub)" />
              <span v-else class="market-badge" :class="marketBadgeClass(sub)">
                {{ marketLabel(sub) }}
              </span>
            </div>
            <div class="table-cell bet">{{ sub.derived ? '—' : formatBetAmount(sub.amount) }}</div>
            <div class="table-cell odds">{{ subOddsText(sub) }}</div>
            <div class="table-cell total" :class="subIsPending(sub) ? 'pending' : sub.derived ? '' : signClass(sub.winLossAmount)">
              {{ subIsPending(sub) ? $t('Pending') : sub.derived ? '—' : formatSigned(Number(sub.winLossAmount)) }}
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { generateTicketData, formatAmount, formatBetAmount } from '@/utils/helpers';
import { PopupPage } from '@/utils/index.model';
import { formatTaiXiuOdds, taiXiuGameOdds, taiXiuIconMarket, taiXiuSelectionMarket } from '@/utils/tai-xiu-market';
import TxMarketBadge from '@/components/tai-xiu/tx-market-badge.vue';
import { getSubTickets, isMultiSelectionTicket, isTicketPending } from '@/utils/ticket-selections';

const COCKFIGHT_SELECTIONS = ['banker', 'player', 'draw'];

export default {
  components: { TxMarketBadge },
  data() {
    return {
      // ticket id -> its selections are showing.
      expanded: {},
    };
  },
  computed: {
    ...mapGetters({
      ticketListing: 'ticket/ticketListing',
      ticketTotals: 'ticket/ticketTotals',
      timezone: 'general/timezone',
      isCreditUser: 'general/withCommission',
      hasCommission: 'general/hasCommission',
      member: 'general/member',
      config: 'general/config',
    }),
    ticketsDisplay() {
      return generateTicketData(this.ticketListing, this.ticketTotals, this.$moment, this.timezone, this.isCreditUser);
    },
    filteredTickets() {
      return this.ticketsDisplay.filter((ticket) => ticket.type === 'detail');
    },
    // Day dividers and bets in order; the stripe restarts under each divider.
    rows() {
      let stripe = 0;
      return this.ticketsDisplay.map((ticket) => {
        if (ticket.type === 'general') {
          stripe = 0;
          return ticket;
        }
        stripe += 1;
        return { ...ticket, stripe: stripe - 1 };
      });
    },
    taxPercent() {
      return this.config.taxPercent;
    },
    currencySymbol() {
      return this.member.currencySymbol;
    },
    showEarnings() {
      return this.config.showEarnings;
    },
  },
  methods: {
    ...mapActions('menu', ['updateMenu']),
    ...mapActions('ticket', ['resetTicket', 'updateTicket']),
    formatAmount,
    formatBetAmount,
    formatTime(value) {
      if (!value) return '';
      return String(value).slice(0, 5);
    },
    // Cockfight tickets fall through to null: no bet type maps for their selections.
    iconMarket(ticket) {
      if (this.isCockfightTicket(ticket)) return null;
      return taiXiuIconMarket(ticket.raw.betType, ticket.raw.selection);
    },
    // Tài Xỉu tickets carry Euro odds; shown as game odds, as the bet detail
    // shows them (1.97 -> 1:0.97), without its spaces to fit the column.
    oddsText(ticket) {
      const value = this.isCockfightTicket(ticket) ? null : taiXiuGameOdds(ticket.odds);
      return value === null ? ticket.odds : formatTaiXiuOdds(value).replace(/ /g, '');
    },
    isCockfightTicket(ticket) {
      return COCKFIGHT_SELECTIONS.includes(String((ticket.raw || {}).selection || '').toLowerCase());
    },
    isMulti(ticket) {
      return isMultiSelectionTicket(ticket.raw);
    },
    subTickets(ticket) {
      return getSubTickets(ticket.raw);
    },
    isExpanded(ticket) {
      return !!this.expanded[ticket.raw.id];
    },
    toggleExpanded(ticket) {
      this.$set(this.expanded, ticket.raw.id, !this.expanded[ticket.raw.id]);
    },
    // A selection of a multi-selection ticket, badged and priced as a ticket of its own.
    isCockfightSelection(sub) {
      return COCKFIGHT_SELECTIONS.includes(String(sub.selection || '').toLowerCase());
    },
    subIconMarket(sub) {
      return this.isCockfightSelection(sub) ? null : taiXiuSelectionMarket(sub.betType, sub.selection);
    },
    // Game odds, as the ticket rows show them (1.97 -> 1:0.97, 121 -> 1:120).
    subOddsText(sub) {
      const value = this.isCockfightSelection(sub) ? null : taiXiuGameOdds(sub.odds);
      return value === null ? sub.odds : formatTaiXiuOdds(value).replace(/ /g, '');
    },
    subIsPending(sub) {
      return isTicketPending(sub);
    },
    isPending(ticket) {
      return isTicketPending(ticket.raw || {});
    },
    marketLabel(ticket) {
      const selection = String(ticket.selection || '').toLowerCase();
      // One letter: the full word crowded the table's left columns.
      if (selection === 'banker') return 'B';
      if (selection === 'player') return 'P';
      if (selection === 'draw') return this.$t('Draw').toUpperCase();
      if (['xiu', 'xỉu', 'small'].includes(selection)) return this.$t('Small').toUpperCase();
      if (['tai', 'tài', 'big'].includes(selection)) return this.$t('Big').toUpperCase();
      if (['le', 'lẻ', 'odd'].includes(selection)) return this.$t('Odd').toUpperCase();
      if (['chan', 'chẵn', 'even'].includes(selection)) return this.$t('Even').toUpperCase();
      return selection ? selection.toUpperCase() : '-';
    },
    marketBadgeClass(ticket) {
      const selection = String(ticket.selection || '').toLowerCase();
      if (selection === 'banker') return 'is-banker';
      if (selection === 'player') return 'is-player';
      if (selection === 'draw') return 'is-draw';
      if (['xiu', 'xỉu', 'small'].includes(selection)) return 'is-xiu';
      if (['tai', 'tài', 'big'].includes(selection)) return 'is-tai';
      if (['le', 'lẻ', 'odd'].includes(selection)) return 'is-odd';
      if (['chan', 'chẵn', 'even'].includes(selection)) return 'is-even';
      return 'is-default';
    },
    totalValue(ticket) {
      return this.showEarnings ? ticket.earnings : ticket.wl;
    },
    hasNumericTotal(ticket) {
      return Number(this.totalValue(ticket)) !== 0;
    },
    formatSignedTotal(ticket) {
      const value = Number(this.totalValue(ticket));
      const amount = formatAmount(Math.abs(value), 2);
      if (value > 0) return `+${amount}`;
      if (value < 0) return `-${amount}`;
      return amount;
    },
    totalClass(ticket) {
      const value = Number(this.totalValue(ticket));
      return {
        win: value > 0,
        lose: value < 0,
      };
    },
    // The day's net: its earnings when the operator shows those, else its total.
    dayTotal(day) {
      return Number(this.showEarnings ? day.earnings : day.total) || 0;
    },
    formatSigned(value) {
      const amount = formatAmount(Math.abs(Number(value) || 0), 2);
      if (value > 0) return `+${amount}`;
      if (value < 0) return `-${amount}`;
      return amount;
    },
    signClass(value) {
      return { win: Number(value) > 0, lose: Number(value) < 0 };
    },
    // As v1: the day's span in the member's timezone, e.g. "03/10 (12:00 am) - 03/10 (11:59 pm)".
    realTimeRange(val) {
      const presetTimezone = this.isCreditUser ? 'Africa/Juba' : this.timezone;
      const day = this.$moment(new Date(val)).tz(presetTimezone);
      const dayStart = day.clone().startOf('day').tz(this.timezone).format('DD/MM (h:mm a)');
      const dayEnd = day.clone().endOf('day').tz(this.timezone).format('DD/MM (h:mm a)');
      return `${dayStart} - ${dayEnd}`;
    },
    viewBetDetail(ticket) {
      if (!ticket || ticket.type === 'general') return;
      this.resetTicket();
      this.updateTicket(ticket.raw);
      this.updateMenu({ activePopupPage: PopupPage.betDetail });
    },
  },
};
</script>
<style lang="scss" scoped>
$table-row-height: 48px;
$table-row-padding-x: 12px;
// Round column: the arena and round id.
$round-width: 64px;
// Market column: wide enough for the widest badge ("4:31|17:31", a triple).
$market-width: 72px;

.table {
  width: 100%;
  font-size: 12px;
  font-weight: 500;

  &-header,
  &-row {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    width: 100%;
    padding: 0 $table-row-padding-x;
    box-sizing: border-box;
  }

  &-header {
    min-height: 32px;
    padding-top: 9px;
    padding-bottom: 9px;
    background-color: #1D292A;
    border-radius: 8px 8px 0 0;

    &-cell {
      color: #AC9261;
      text-transform: uppercase;
      font-size: 11px;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
      flex-shrink: 0;
    }
  }

  &-row {
    min-height: $table-row-height;
    height: $table-row-height;
    cursor: pointer;
    background-color: #0b1d31;

    &.even {
      background-color: #10233a;
    }
  }
  .table-body {

    &-cell {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;
    white-space: nowrap;
  }

  // Fixed: the free space belongs to the bet column, which centres the stake in it.
  .round {
    flex: 0 0 $round-width;
    min-width: 0;
    margin-right: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
  }

  .round__id {
    overflow: hidden;
    text-overflow: ellipsis;
    color: #A1BBC9;
  }

  .round__time {
    font-size: 11px;
    color: #6A91A7;
  }

  // Fixed width so every row's bet column (and its centred stake) lines up;
  // the badge sits on its right edge, up against Odds.
  .market {
    flex: 0 0 $market-width;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-right: 4px;
  }

  // Takes the row's free space, the stake centred in it.
  .bet {
    flex: 1 1 0;
    min-width: 60px;
    margin-right: 4px;
    text-align: center;
    color: #fff;
  }

  // 40, not 32: Tài Xỉu odds read "1:0.97".
  .odds {
    width: 40px;
    margin-left: 4px;
    text-align: right;
    white-space: nowrap;
    color: #6a91a7;
  }

  .total {
    width: 64px;
    margin-left: 4px;
    text-align: right;
    color: #fff;

    &.win {
      color: #44f589;
    }

    &.lose {
      color: #e85050;
    }

    .result-status {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 20px;
      padding: 2px 6px;
      border-radius: 4px;
      text-transform: uppercase;
      font-size: 10px;
      font-weight: 700;
    }

    .draw {
      background-color: #0c836c;
      color: #fff;
    }

    .cancelled {
      color: #6c6030;
      background-color: #d8c7a0;
    }

    .void {
      background-color: #3c3a37;
      color: #fff;
    }
  }
  }


  /*
   * Multi-selection ticket (Multi-Selection Ticket Prototype v1.0): the row
   * itself as any other (same stripes), with a gold "N BETS" pill, "—" for
   * odds and PENDING with a chevron; its selections in darker rows below.
   */

  .bets-toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 28px;
    padding: 0 10px;
    border: 2px solid #d9b25a;
    border-radius: 999px;
    background: transparent;
    color: #efc87f;
    font-family: inherit;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;
    box-sizing: border-box;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    &.open {
      background: rgba(255, 255, 255, 0.12);
    }

    // Solid triangle: down while closed, up while open.
    &__caret {
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 5px solid currentColor;
      transition: transform 0.2s;
    }

    &.open &__caret {
      transform: rotate(180deg);
    }
  }

  .odds-multi {
    color: #8ea0bd;
  }

  .multi-status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #8ea0bd;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;

    &__chevron {
      font-size: 14px;
      line-height: 1;
    }
  }

  .sub-rows {
    background-color: #0d1830;
  }

  // One selection: its badge, stake, odds and W/L, under the parent's columns.
  .sub-row {
    display: flex;
    align-items: center;
    min-height: 46px;
    padding: 6px $table-row-padding-x 6px ($table-row-padding-x + 8px);
    box-sizing: border-box;
    border-bottom: 1px dashed #2a3f63;

    &:last-child {
      border-bottom: 0;
    }

    &__market {
      flex: 1 1 0;
      min-width: 0;
      display: flex;
      align-items: center;
    }

    .bet {
      color: #fff;
      text-align: right;
    }

    .odds {
      color: #8ea0bd;
      text-align: center;
    }

    .total {
      font-weight: 700;
    }

    .total.pending {
      color: #8ea0bd;
      font-size: 11px;
      text-transform: uppercase;
    }

    // Bigger badges than the table's: a 28px gold-rimmed total, flat BIG / EVEN.
    ::v-deep .tx-market-badge--total {
      height: 28px;
      min-width: 44px;
      padding: 0 8px;
      gap: 6px;
      border-width: 2px;
      border-radius: 7px;
      background: #1b2a4f;
      font-size: 14px;
    }

    .market-badge {
      height: 26px;
      max-width: none;
      padding: 0 10px;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 800;

      &.is-tai {
        background: #d4473c;
      }

      &.is-xiu {
        background: #2f74c8;
      }

      &.is-odd,
      &.is-even {
        background: #4c9d6a;
      }
    }
  }

  .market-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    min-width: 32px;
    max-width: 52px;
    padding: 0 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    box-sizing: border-box;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.is-banker {
      background-color: #eb3323;
    }

    &.is-player {
      background-color: #3996e5;
    }

    &.is-draw {
      background-color: #0c836c;
    }

    // Tài Xỉu text markets wear the bet board's own zone colours (bet-board.vue).
    &.is-xiu {
      background: linear-gradient(200deg, #3a86dc, #1f62b8 50%, #0d3f7a);
    }

    &.is-tai {
      background: linear-gradient(160deg, #c33a2f, #a01f1f 55%, #6b1414);
    }

    &.is-odd,
    &.is-even {
      background: linear-gradient(180deg, #24a35c, #157140);
    }

    &.is-default {
      background-color: #24475c;
      color: #b2d6ea;
    }
  }

  .table-header-cell {
    &.bet {
      flex: 1 1 0;
      min-width: 60px;
      margin-right: 4px;
      text-align: center;
    }

    &.odds {
      width: 40px;
      margin-left: 4px;
      text-align: right;
    }

    &.total {
      width: 64px;
      margin-left: 4px;
      text-align: right;
    }

    &.round {
      flex: 0 0 $round-width;
      min-width: 0;
      margin-right: 4px;
    }

    &.market {
      flex: 0 0 $market-width;
      min-width: 0;
      margin-right: 4px;
    }
  }
}

/*
 * Date divider (v1's day block): darker than the bet rows so each day reads as
 * its own section, in the table's own palette.
 */
.day-divider {
  padding: 12px $table-row-padding-x;
  background-color: #071522;
  border-top: 1px solid #1d2c44;
  border-bottom: 1px solid #1d2c44;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  color: #fff;

  .win {
    color: #44f589;
  }

  .lose {
    color: #e85050;
  }
}

.day-divider__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

// Even columns with breathing room; the last one (W/L) sits on the right edge.
.day-divider__details {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  column-gap: 10px;
  margin-top: 5px;
  // padding-top: 5px;
  // border-top: 1px dashed rgba(106, 145, 167, 0.2);
}

.day-divider__date {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  color: #ceb57d;
  font-size: 13px;
  font-weight: 700;
}

.day-divider__range {
  font-size: 10px;
  font-weight: 500;
  color: #a68f5c;
  white-space: nowrap;
}

.day-divider__total {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.day-divider__item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  white-space: nowrap;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &--end {
    text-align: right;
  }
}

.day-divider__label {
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #6a91a7;
  text-transform: uppercase;
}
</style>
